import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Segmented, Spin, Switch} from 'antd';
import LightweightCandlestickChart, {KLineBar} from '../../../components/charts/LightweightCandlestickChart';
import CompactIndicatorBar from '../../trading/components/CompactIndicatorBar';
import {IndicatorConfig, TechnicalIndicator} from '../../../hooks/useChartState';
import {getResearchKline} from '../../../services/researchReportService';
import type {CompleteChartData, MarkPriceCandle} from '../../../services/tradingService';
import type {LevelNode} from '../../../types/researchReport';

/**
 * 研报 K 线 tab（历史回溯）：读研报生成时刻的快照（数据库存取），
 * 指标为固定口径（EMA/BOLL/RSI/MACD/KDJ）只读展示——不可增删指标、不可改周期
 */

/** 时间帧选项：展示 5m/1H/1D，请求后端用小写（后端查表归一化） */
const TIMEFRAME_OPTIONS = [
    {label: '5m', value: '5m'},
    {label: '1H', value: '1h'},
    {label: '1D', value: '1d'},
];

/** 图表高度（px） */
const CHART_HEIGHT = 520;

/** K线根数（快照口径） */
const KLINE_LIMIT = 240;

/** 固定指标口径（与后端快照采集一致）：EMA(20,50) · BOLL(20,2) · RSI(14) · MACD(12,26,9) · KDJ(9) */
const FIXED_INDICATORS: TechnicalIndicator[] = ['EMA', 'BOLL', 'RSI', 'MACD', 'KDJ'];
const FIXED_INDICATOR_CONFIGS = {
    EMA: {period: 20, periods: [20, 50], visiblePeriods: [20, 50]},
    BOLL: {period: 20, stdDev: 2, periods: [20], visiblePeriods: [20], stdDevs: [2]},
    RSI: {period: 14, periods: [14], visiblePeriods: [14]},
    MACD: {period: 12, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9},
    KDJ: {period: 9, periods: [9], visiblePeriods: [9]},
} as Record<TechnicalIndicator, IndicatorConfig>;

/** MACD 标准参数（软开关恢复用） */
const MACD_DEFAULT_CONFIG = {period: 12, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9};

interface ResearchKlineTabProps {
    /** 研报ID（快照按批次读取） */
    reportId?: number;
    /** true=永续（-USDT-SWAP），false=现货（-USDT） */
    swap: boolean;
    /** 研报价位表（叠加支撑/阻力参考线） */
    levels: LevelNode[];
}

const normalizeNumber = (value: unknown): number | undefined => {
    if (null == value || '' === value) return undefined;
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : undefined;
};

/** K线转图表 bars（过滤无效点并按时间升序） */
const candlesToBars = (candles: MarkPriceCandle[]): KLineBar[] =>
    (candles ?? [])
        .filter(c => c && Number.isFinite(c.timestamp))
        .map(c => ({
            time: c.timestamp,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
            volume: c.volume || 0,
            confirmed: 1 === c.confirm,
        }))
        .sort((a, b) => a.time - b.time);

/** 后端指标（multiPeriodValues 结构）→ 图表扁平序列（任意指标 + 多周期） */
const transformIndicators = (indicators: CompleteChartData['indicators']) => {
    if (!indicators) return undefined;
    const result: Record<string, any[]> = {};
    Object.entries(indicators).forEach(([indicatorKey, indicator]) => {
        const values = (indicator as any)?.values;
        if (!Array.isArray(values)) return;
        values.forEach((point: any) => {
            const multiPeriod = point?.multiPeriodValues;
            if (!multiPeriod || 'object' !== typeof multiPeriod) return;
            const timestamp = normalizeNumber(point.timestamp);
            if (undefined === timestamp) return;
            Object.entries(multiPeriod).forEach(([periodKey, periodData]: [string, any]) => {
                if (null == periodData) return;
                const seriesKey = `${indicatorKey}_${periodKey}`;
                if ('BOLL' === indicatorKey) {
                    const upper = normalizeNumber(periodData.upper);
                    const middle = normalizeNumber(periodData.middle);
                    const lower = normalizeNumber(periodData.lower);
                    if (undefined === upper && undefined === middle && undefined === lower) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({time: timestamp, upper, middle, lower});
                } else if ('MACD' === indicatorKey) {
                    const diff = normalizeNumber(periodData.diff);
                    const signal = normalizeNumber(periodData.dea);
                    const histogram = normalizeNumber(periodData.macd);
                    if (undefined === diff && undefined === signal && undefined === histogram) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({time: timestamp, diff, signal, histogram});
                } else if ('KDJ' === indicatorKey) {
                    const k = normalizeNumber(periodData.k);
                    const d = normalizeNumber(periodData.d);
                    const j = normalizeNumber(periodData.j);
                    if (undefined === k && undefined === d && undefined === j) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({time: timestamp, k, d, j});
                } else {
                    const value = normalizeNumber(periodData);
                    if (undefined === value) return;
                    if (!result[seriesKey]) result[seriesKey] = [];
                    result[seriesKey].push({time: timestamp, value});
                }
            });
        });
    });
    Object.values(result).forEach(arr => arr.sort((a: any, b: any) => a.time - b.time));
    return Object.keys(result).length ? result : undefined;
};

/** 研报价位 → 支撑/阻力参考线（永续优先 swapPrice，回退现货价；当前价不画线） */
const levelsToReferenceLines = (levels: LevelNode[], swap: boolean) => {
    const supportLines: Array<{ price: number; label?: string }> = [];
    const resistanceLines: Array<{ price: number; label?: string }> = [];
    (levels ?? []).forEach(level => {
        const raw = swap && level.swapPrice ? level.swapPrice : level.price;
        const price = normalizeNumber(raw);
        if (undefined === price) return;
        if ('support' === level.type) {
            supportLines.push({price, label: level.role});
        } else if ('resistance' === level.type) {
            resistanceLines.push({price, label: level.role});
        }
    });
    if (0 === supportLines.length && 0 === resistanceLines.length) return undefined;
    return {supportLines, resistanceLines};
};

const ResearchKlineTab: React.FC<ResearchKlineTabProps> = ({reportId, swap, levels}) => {
    const [timeframe, setTimeframe] = useState<string>('1h');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [chartData, setChartData] = useState<CompleteChartData | null>(null);
    /** 指标配置（受控）：周期值面板只读不可改，仅可见性（visiblePeriods / MACD 软开关）可变 */
    const [indicatorConfigs, setIndicatorConfigs] =
        useState<Record<TechnicalIndicator, IndicatorConfig>>(FIXED_INDICATOR_CONFIGS);
    /** tag 恒显固定 5 项（隐藏态通过配置软开关表达，保证可点回恢复） */
    const selectedIndicators = FIXED_INDICATORS;
    /** 参考线显示开关：支撑位、阻力/目标位各自独立控制（默认均显示） */
    const [showSupports, setShowSupports] = useState(true);
    const [showResistances, setShowResistances] = useState(true);

    /**
     * tag 点击：MACD 通过配置软开关隐藏/显示（tag 恒显可点回）；非 MACD 由组件内部走
     * onUpdateIndicatorConfig 切换 visiblePeriods，无需在此处理
     */
    const handleToggleIndicator = useCallback((type: TechnicalIndicator) => {
        if ('MACD' !== type) return;
        setIndicatorConfigs(prev => {
            const config = prev.MACD;
            const hasConfig = !!(config?.fastPeriod || config?.slowPeriod);
            return {
                ...prev,
                MACD: hasConfig
                    ? {...config, fastPeriod: undefined, slowPeriod: undefined, signalPeriod: undefined}
                    : {...config, ...MACD_DEFAULT_CONFIG},
            };
        });
    }, []);

    /** 配置更新：visiblePeriods 等由 tag/面板勾选触发（周期值输入框只读不会变） */
    const handleUpdateIndicatorConfig = useCallback((type: TechnicalIndicator, partial: Partial<IndicatorConfig>) => {
        setIndicatorConfigs(prev => ({...prev, [type]: {...prev[type], ...partial}}));
    }, []);

    useEffect(() => {
        if (null == reportId) {
            setChartData(null);
            setError('该研报暂无 K 线数据');
            return;
        }
        let active = true;
        setLoading(true);
        setError('');
        getResearchKline(reportId, swap, timeframe)
            .then(data => {
                if (!active) return;
                if (null == data) {
                    setChartData(null);
                    setError('该研报暂无 K 线数据（生成于快照功能上线前）');
                } else {
                    setChartData(data);
                }
            })
            .catch((e: any) => {
                if (active) {
                    setChartData(null);
                    setError(e?.message ?? 'K线数据加载失败');
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [reportId, swap, timeframe]);

    const bars = useMemo(() => candlesToBars(chartData?.candles ?? []), [chartData]);
    const indicators = useMemo(() => transformIndicators(chartData?.indicators), [chartData]);
    // 参考线：按开关过滤支撑/阻力（含中期目标）两组价位线
    const referenceLines = useMemo(() => {
        const lines = levelsToReferenceLines(levels, swap);
        if (!lines) return undefined;
        const supportLines = showSupports ? lines.supportLines : [];
        const resistanceLines = showResistances ? lines.resistanceLines : [];
        if (0 === supportLines.length && 0 === resistanceLines.length) return undefined;
        return {supportLines, resistanceLines};
    }, [levels, swap, showSupports, showResistances]);

    // 可见指标（与交易图表同款 key 生成规则；MACD 依配置存在性判断显隐，其余依 visiblePeriods）
    const visibleIndicators = useMemo(() => {
        const result: Record<string, boolean> = {};
        selectedIndicators.forEach(indicator => {
            const config = indicatorConfigs[indicator];
            if ('MACD' === indicator) {
                const fastPeriod = config?.fastPeriod;
                const slowPeriod = config?.slowPeriod;
                const signalPeriod = config?.signalPeriod;
                if (fastPeriod && slowPeriod && signalPeriod) {
                    result[`MACD_${fastPeriod}_${slowPeriod}_${signalPeriod}`] = true;
                }
                return;
            }
            const periodsToShow = config?.visiblePeriods !== undefined ? config.visiblePeriods : (config?.periods || []);
            periodsToShow.forEach(period => {
                result[`${indicator}_${period}`] = true;
            });
        });
        return result;
    }, [indicatorConfigs]);

    return (
        <div className="rr-kline-tab">
            <div className="rr-kline-toolbar">
                <Segmented options={TIMEFRAME_OPTIONS} value={timeframe}
                           onChange={value => setTimeframe(String(value))}/>
                <span className="rr-kline-toggle">
                    <Switch size="small" checked={showSupports} onChange={setShowSupports}/>
                    <span>支撑位</span>
                </span>
                <span className="rr-kline-toggle">
                    <Switch size="small" checked={showResistances} onChange={setShowResistances}/>
                    <span>阻力/目标</span>
                </span>
                <CompactIndicatorBar
                    selectedIndicators={selectedIndicators}
                    indicatorConfigs={indicatorConfigs}
                    onToggleIndicator={handleToggleIndicator}
                    onUpdateIndicatorConfig={handleUpdateIndicatorConfig}
                    readonly
                    hideAdd
                />
            </div>
            <div className="rr-kline-chart">
                {loading ? <div className="rr-kline-mask"><Spin/></div> : null}
                {!loading && error ? <div className="rr-kline-mask rr-kline-error">{error}</div> : null}
                {!loading && !error && 0 === bars.length ? (
                    <div className="rr-kline-mask rr-kline-error">暂无K线数据</div>
                ) : null}
                {0 < bars.length ? (
                    <LightweightCandlestickChart
                        data={bars}
                        indicators={indicators}
                        visibleIndicators={visibleIndicators}
                        timeFrame={timeframe}
                        maxVisibleBars={KLINE_LIMIT}
                        height={CHART_HEIGHT}
                        referenceLines={referenceLines}
                    />
                ) : null}
            </div>
        </div>
    );
};

export default ResearchKlineTab;
